import { Box, Typography, Button, Container, Grid } from "@mui/material";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { fetchSubscriptionPlans } from "services/auth";

const PLAN_ICONS = ["👥", "🌍", "🚀", "💎"];

const DEFAULT_FEATURES = [
  "Unlimited clients",
  "Nutrition plan templates",
  "Invoicing & payments",
  "Progress tracking",
  "WhatsApp + Email support",
];

const DEMO_PLAN = {
  id: "demo",
  name: "Demo",
  icon: "✨",
  tagline: "Try Fitify for free to test it in work",
  price: "Free",
  targetAudience: "For: Beginners / Solo dietitians testing the platform",
  description: "Get started with core features at no cost.",
  features: [
    "Up to 10 clients",
    "Basic client records",
    "Appointment scheduling",
    "WhatsApp support",
  ],
  buttonText: "Get Started Free",
  buttonColor: "#17c1e8",
};

// Set to true on the home page to temporarily hide the 1-month plan card.
const HIDE_ONE_MONTH_PLAN = false;

const PricingSection = ({
  title,
  subtitle,
  description,
  hideOneMonthPlan = HIDE_ONE_MONTH_PLAN,
}) => {
  const navigate = useNavigate();
  const [firebasePlans, setFirebasePlans] = useState([]);

  useEffect(() => {
    fetchSubscriptionPlans()
      .then((subs) => setFirebasePlans(subs))
      .catch(() => setFirebasePlans([]));
  }, []);

  const handlePlanClick = (planId) => {
    if (planId === "demo") {
      const el = document.getElementById("book-demo");
      if (el) {
        el.scrollIntoView({ behavior: "smooth" });
      } else {
        navigate("/", { state: { scrollTo: "book-demo" } });
      }
    } else {
      navigate("/register");
    }
  };

  const firebaseCards = [...firebasePlans]
    .filter((plan) => !(hideOneMonthPlan && plan.months === 1))
    .sort((a, b) => a.months - b.months)
    .map((plan, idx) => ({
      id: plan.id || idx + 1,
      name: plan.plan,
      icon: PLAN_ICONS[idx % PLAN_ICONS.length],
      tagline: "Unlock all premium features",
      price: `₹${plan.paymentAmount}`,
      targetAudience: `For: ${plan.months} month${plan.months > 1 ? "s" : ""} subscription`,
      description: "Scale your practice with ease.",
      features: DEFAULT_FEATURES,
      buttonText: `Start ${plan.plan}`,
      buttonColor: "#17c1e8",
    }));

  const plansData = [DEMO_PLAN, ...firebaseCards];

  return (
    <Box sx={{ py: { xs: 6, md: 8 } }}>
      <Container maxWidth="xl">
        {/* Header Section */}
        <Box sx={{ textAlign: "center", mb: { xs: 4, md: 6 } }}>
          <Typography
            variant="h6"
            sx={{
              color: "#17c1e8",
              fontSize: { xs: "1rem", md: "1.1rem" },
              fontWeight: 500,
              mb: 2,
              fontFamily: "Outfit",
            }}
          >
            {subtitle || "Simple & Transparent Pricing"}
          </Typography>

          <Typography
            variant="h2"
            sx={{
              color: "#FFFFFF",
              fontSize: {
                xs: "1.8rem",
                sm: "2.2rem",
                md: "2.8rem",
                lg: "3.2rem",
              },
              fontWeight: 600,
              mb: 2,
              lineHeight: 1.2,
              fontFamily: "Outfit",
            }}
          >
            {title || "Find the perfect plan for your growth journey"}
          </Typography>

          <Typography
            variant="h6"
            sx={{
              color: "rgba(255, 255, 255, 0.8)",
              fontWeight: 400,
              margin: "0 auto",
              lineHeight: 1.6,
              fontSize: { xs: "0.95rem", md: "1.1rem" },
              fontFamily: "Outfit",
              maxWidth: "600px",
            }}
          >
            {description ||
              "Whether you're just getting started or scaling your business, Fitify grows with you."}
          </Typography>
        </Box>

        {/* Pricing Cards */}
        <Grid container spacing={3} sx={{ justifyContent: "center" }}>
          {plansData.map((plan) => (
            <Grid item xs={12} sm={6} lg={3} key={plan.id}>
              <Box
                sx={{
                  backgroundColor: "rgba(45, 45, 45, 0.8)",
                  borderRadius: "20px",
                  padding: { xs: 3, md: 4 },
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  backdropFilter: "blur(10px)",
                  border: "1px solid rgba(255, 255, 255, 0.1)",
                  boxShadow: "0 20px 40px rgba(0, 0, 0, 0.3)",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-5px)",
                    boxShadow: "0 30px 60px rgba(0, 0, 0, 0.4)",
                  },
                }}
              >
                {/* Plan Icon */}
                <Box
                  sx={{
                    fontSize: "2rem",
                    mb: 2,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  {plan.icon}
                </Box>

                {/* Plan Name */}
                <Typography
                  variant="h4"
                  sx={{
                    color: "#FFFFFF",
                    fontSize: { xs: "1.5rem", md: "1.8rem" },
                    fontWeight: "bold",
                    mb: 1,
                    fontFamily: "Outfit",
                  }}
                >
                  {plan.name}
                </Typography>

                {/* Tagline */}
                <Typography
                  sx={{
                    color: "rgba(255, 255, 255, 0.8)",
                    fontSize: { xs: "0.9rem", md: "1rem" },
                    mb: 2,
                    fontFamily: "Outfit",
                  }}
                >
                  {plan.tagline}
                </Typography>

                {/* Price */}
                <Typography
                  variant="h3"
                  sx={{
                    color: "#FFFFFF",
                    fontSize: { xs: "2.5rem", md: "3rem" },
                    fontWeight: "bold",
                    mb: 2,
                    fontFamily: "Outfit",
                  }}
                >
                  {plan.price}
                </Typography>

                {/* Target Audience */}
                <Typography
                  sx={{
                    color: "#FFFFFF",
                    fontSize: { xs: "0.85rem", md: "0.9rem" },
                    fontWeight: 500,
                    mb: 1,
                    fontFamily: "Outfit",
                  }}
                >
                  {plan.targetAudience}
                </Typography>

                {/* Description */}
                <Typography
                  sx={{
                    color: "rgba(255, 255, 255, 0.8)",
                    fontSize: { xs: "0.9rem", md: "1rem" },
                    mb: 3,
                    fontFamily: "Outfit",
                  }}
                >
                  {plan.description}
                </Typography>

                {/* Features List */}
                <Box sx={{ flex: 1, mb: 3 }}>
                  {plan.features.map((feature, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        mb: 1.5,
                      }}
                    >
                      <Box
                        sx={{
                          width: "6px",
                          height: "6px",
                          borderRadius: "50%",
                          backgroundColor: "#17c1e8",
                          mr: 2,
                          flexShrink: 0,
                        }}
                      />
                      <Typography
                        sx={{
                          color: "#FFFFFF",
                          fontSize: { xs: "0.85rem", md: "0.9rem" },
                          fontFamily: "Outfit",
                        }}
                      >
                        {feature}
                      </Typography>
                    </Box>
                  ))}
                </Box>

                {/* Call to Action Button */}
                <Button
                  onClick={() => handlePlanClick(plan.id)}
                  sx={{
                    backgroundColor: plan.buttonColor,
                    color: "#242424",
                    padding: { xs: "12px 24px", md: "16px 32px" },
                    borderRadius: "25px",
                    fontSize: { xs: "0.9rem", md: "1rem" },
                    fontWeight: 400,
                    textTransform: "none",
                    fontFamily: "Outfit",
                    width: "100%",
                    "&:hover": {
                      backgroundColor: "#00bcd4",
                      color: "#000000",
                      transform: "translateY(-2px)",
                    },
                    "&:active": {
                      backgroundColor: plan.buttonColor,
                      color: "#242424",
                    },
                    "&:focus-visible": {
                      backgroundColor: plan.buttonColor,
                      color: "#242424",
                    },
                    transition: "all 0.3s ease",
                  }}
                >
                  {plan.buttonText}
                </Button>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

PricingSection.propTypes = {
  title: PropTypes.string,
  subtitle: PropTypes.string,
  description: PropTypes.string,
  hideOneMonthPlan: PropTypes.bool,
};

export default PricingSection;
